import { MaIcon, type IconName } from "./MaIcon";

interface Props {
  label?: string;
  height?: number | string;
  ratio?: string;
  tone?: "mustard" | "brick" | "canal" | "pink" | "sky" | "ink";
  src?: string | null;
  /** Renders a category-icon watermark + venue initial when there's no
   *  real image — so different venues read as distinct instead of
   *  identical "logo" tiles. Ignored when `src` resolves. */
  category?: string | null;
  fallbackInitial?: string | null;
}

const TONE_VARS: Record<string, string> = {
  mustard: "var(--ma-mustard)",
  brick: "var(--ma-brick)",
  canal: "var(--ma-canal)",
  pink: "var(--ma-pink)",
  sky: "var(--ma-sky)",
  ink: "var(--ma-ink)",
};

// Map our 10 categories to existing MaIcon names.
const CAT_ICON: Record<string, IconName> = {
  music: "music", food: "food", art: "art", market: "market",
  cinema: "cinema", outdoor: "outdoor", cafe: "cafe",
  queer: "queer", sports: "sports", hidden: "hidden",
};

export function MaImageSlot({ label, height = 120, ratio, tone = "mustard", src, category, fallbackInitial }: Props) {
  const c = TONE_VARS[tone] ?? TONE_VARS.mustard;
  // Soft radial wash — the deliberate background for the no-image state.
  const placeholder = `radial-gradient(120% 80% at 30% 20%, ${c}, var(--ma-ink))`;
  const icon: IconName | null = category ? CAT_ICON[String(category)] ?? null : null;
  const initial = fallbackInitial?.trim().charAt(0).toUpperCase() ?? null;
  const heightPx = typeof height === "number" ? height : 0;

  return (
    <div style={{
      width: "100%", height: ratio ? undefined : height, aspectRatio: ratio,
      background: placeholder,
      position: "relative", overflow: "hidden",
      borderRadius: "var(--ma-card-radius)",
    }}>
      {/* Watermark used when no real image — keep this in the DOM so the
          image can fade in over it without flashing the empty gradient. */}
      {(icon || initial) && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--ma-paper)", opacity: 0.5,
          pointerEvents: "none",
        }}>
          {initial ? (
            <span className="ma-display" style={{
              fontSize: heightPx ? Math.max(20, heightPx * 0.55) : 48,
              lineHeight: 0.85,
              fontWeight: 800,
              letterSpacing: "-0.02em",
              textShadow: "0 2px 8px rgba(0,0,0,0.25)",
            }}>{initial}</span>
          ) : icon ? (
            <MaIcon name={icon} size={heightPx ? Math.max(20, heightPx * 0.45) : 40} stroke={1.6} color="var(--ma-paper)" />
          ) : null}
        </div>
      )}

      {src && (
        <img src={src} alt={label ?? ""} loading="lazy" referrerPolicy="no-referrer"
          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover",
          }} />
      )}
      {label && (
        <div style={{
          position: "absolute", left: 8, bottom: 8,
          padding: "3px 6px", background: "var(--ma-ink)", color: "var(--ma-paper)",
          fontFamily: "var(--ma-mono)", fontSize: 9, letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}>{label}</div>
      )}
    </div>
  );
}
